import { useEffect, useState, type ReactNode } from 'react';
import { CheckCircle, Loader2 } from 'lucide-react';
import { offreRetenueApi, stripeApi } from '@/services/api';
import { RenonciationRetractation } from '@/components/RenonciationRetractation';
import { FORMULE_RETENUE, prixParPeriode } from '@/lib/tarifs';

const CONTENU_FORMULE = [
    'Échéances et alertes',
    'Point mensuel par courriel',
    'Lettre de réclamation',
    'Libération des retenues',
];

/**
 * Le tarif de la formule Retenue, proposé à un compte Retenue (fenêtre des tarifs,
 * écran de blocage). Le serveur choisit seul 39 € ou 59 € par an au moment du
 * paiement : l'écran annonce le prix selon les places fondateur restantes, et
 * prévient que le prix exact s'affiche sur la page de paiement.
 */
export function TarifRetenue(): ReactNode {
    // `null` tant que la lecture n'est pas revenue. Une lecture en échec vaut zéro
    // place : on n'annonce jamais un prix fondateur qu'on ne peut pas garantir.
    const [places, setPlaces] = useState<number | null>(null);
    const [renonciation, setRenonciation] = useState(false);
    const [paiementEnCours, setPaiementEnCours] = useState(false);
    const [erreur, setErreur] = useState('');

    useEffect(() => {
        let actif = true;
        offreRetenueApi.placesFondateur()
            .then((n) => { if (actif) setPlaces(n); })
            .catch(() => { if (actif) setPlaces(0); });
        return () => { actif = false; };
    }, []);

    const fondateur = places !== null && places > 0;
    const prix = fondateur ? FORMULE_RETENUE.fondateur : FORMULE_RETENUE.normal;

    const payer = async () => {
        setPaiementEnCours(true);
        setErreur('');
        try {
            const { url } = await stripeApi.createCheckoutSession('retenue', true);
            if (url) window.location.href = url;
        } catch (err: unknown) {
            const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
                || 'Une erreur est survenue. Réessayez ou contactez-nous à contact@btpal.fr.';
            setErreur(message);
            setPaiementEnCours(false);
        }
    };

    return (
        <div>
            {erreur && (
                <div className="mb-4 p-3 bg-[#FEF2F2] border border-[#FECACA] rounded-lg text-sm text-[#991B1B]">
                    {erreur}
                </div>
            )}

            <div className="border-2 border-[#0F2744] rounded-lg p-4 bg-[#EFF6FF] mb-4">
                <p className="text-[10px] font-bold text-[#1D4ED8] uppercase tracking-wider mb-2">Formule Retenue de garantie</p>
                {places === null ? (
                    <p className="text-sm text-[#64748B] flex items-center gap-2 min-h-12">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Lecture du tarif…
                    </p>
                ) : fondateur ? (
                    <>
                        <p className="text-xl font-bold text-[#1E293B]">
                            {`${prixParPeriode(FORMULE_RETENUE.fondateur, 'an')}, prix fondateur garanti à vie tant que votre abonnement continue`}
                        </p>
                        <p className="text-xs text-[#10B981] font-semibold mt-1">
                            {`Il reste ${places} place${places > 1 ? 's' : ''} sur ${FORMULE_RETENUE.placesFondateur}, attribuée${places > 1 ? 's' : ''} automatiquement. Les suivants paieront ${prixParPeriode(FORMULE_RETENUE.normal, 'an')}.`}
                        </p>
                    </>
                ) : (
                    <>
                        <p className="text-2xl font-bold text-[#1E293B]">{prixParPeriode(FORMULE_RETENUE.normal, 'an')}</p>
                        <p className="text-xs text-[#64748B] mt-1">Payé une fois par an, soit moins de 5{' '}€ par mois.</p>
                    </>
                )}
                <button
                    type="button"
                    onClick={payer}
                    disabled={places === null || !renonciation || paiementEnCours}
                    className="mt-4 w-full py-2 rounded-lg bg-[#0F2744] text-white text-sm font-semibold hover:bg-[#1A3A5C] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                    {paiementEnCours && <Loader2 className="h-4 w-4 animate-spin" />}
                    {`S'abonner, ${prix}\u00a0€ par an`}
                </button>
            </div>

            <p className="text-[11px] text-[#64748B] mb-1 px-1">Le prix exact s'affiche sur la page de paiement avant de payer.</p>
            <p className="text-[11px] text-[#64748B] mb-4 px-1">TVA non applicable (article 293 B du CGI).</p>

            <RenonciationRetractation
                acceptee={renonciation}
                onChange={(acceptee) => {
                    setRenonciation(acceptee);
                    if (acceptee) setErreur('');
                }}
            />

            <div className="bg-[#F8FAFC] rounded-lg px-4 py-3 grid grid-cols-2 gap-x-4 gap-y-1.5">
                {CONTENU_FORMULE.map((ligne) => (
                    <div key={ligne} className="flex items-center gap-1.5 text-xs text-[#374151]">
                        <CheckCircle className="h-3.5 w-3.5 text-[#10B981] shrink-0" />
                        <span>{ligne}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}
